<template>
  <div class="d-flex align-items-center w-100">
    <b-form-group class="flex-grow-1" :label="translations.filters.client">
      <multiselect
        v-model="filter_set.clients"
        :options="filter_options.clients"
        :multiple="true"
        :showLabels="false"
        label="text"
        track-by="value"
        :disabled="loading"
        :allow-empty="false"
        @select="checkInput('clients', ...arguments)"/>
    </b-form-group>
    <b-form-group class="flex-grow-1" :label="translations.filters.status">
      <multiselect
        v-model="filter_set.status"
        :options="filter_options.status"
        :multiple="true"
        :showLabels="false"
        label="text"
        track-by="value"
        :disabled="loading"
        :allow-empty="false"
        @select="checkInput('status', ...arguments)"/>
    </b-form-group>
    <b-form-group :label="translations.filters.start_end_date" class="flex-grow-1">
      <vue-ctk-date-time-picker format="YYYY-MM-DD"
        id="start-end-datepicker"
        :disabled="loading"
        :label="translations.filters.start_end_date_placeholder"
        formatted="ll"
        range
        :error="!dateRangeValidator(filter_set.start_end_date)"
        only-date
        v-model="filter_set.start_end_date"/>
    </b-form-group>
    <b-button
      class="mt-2"
      variant="primary"
      :disabled="loading"
      @click="applyFilters">
      <feather type="search"></feather>
    </b-button>
  </div>
</template>

<script>
import translations from '@/translations';
import Multiselect from 'vue-multiselect';
import service from '@/services/finance-service';

export default {
  name: 'FinanceClaimsBatchesListFilters',
  components: {
    Multiselect,
  },
  data() {
    return {
      loading: false,
      translations: translations.finance.claims_batches,
      filter_options: {
        status: [],
        clients: [],
      },
      filter_set: {
        status: [],
        clients: [],
        start_end_date: null,
      },
    };
  },
  methods: {
    // this method is called from the parent component (mounted hook)
    async recoverFiltersFromURL(filters = {}) {
      this.loading = true;
      await Promise.all([
        this.fetchClients(),
        this.loadClaimsBatchesOverview(),
      ]);

      if (filters.status_in?.length > 0) {
        this.filter_set.status = filters.status_in.map(s => {
          const sOption = this.filter_options.status.find(opt => opt.value === s);
          if (!sOption) {
            return null;
          }
          return sOption;
        }).filter(s => !!s);
      }

      if (filters.client_ids?.length > 0) {
        this.filter_set.clients = filters.client_ids.map(s => {
          const sOption = this.filter_options.clients.find(opt => opt.value === Number.parseInt(s, 10));
          if (!sOption) {
            return null;
          }
          return sOption;
        }).filter(s => !!s);
      }

      this.filter_set.start_end_date = filters.start_date_from ? {
        start: filters.start_date_from,
        end: filters.end_date_until || null,
      } : null;

      this.loading = false;
    },
    async fetchClients() {
      try {
        const clients = await this.$store.dispatch('Core/fetchClients');
        const clientsSorted = clients?.sort((a, b) => a.display_name.localeCompare(b.display_name));
        const clientsOptions = clientsSorted.map(client => ({
          value: client.id,
          text: `${client.display_name} (ID: ${client.id})`,
        }));

        this.filter_options.clients = [
          { value: '', text: this.translations.filters.all_default },
          ...clientsOptions,
        ];
      } catch (_) {
        this.$noty.error(this.translations.errors.fetch_clients_error);
      }
    },
    async loadClaimsBatchesOverview() {
      try {
        const claimsBatchesOverview = (await service.getClaimsBatchesOverview()).data.data;
        this.filter_options.status = [
          { value: '', text: this.translations.filters.all_default },
          ...this.calculateStatusOptions(claimsBatchesOverview),
        ];
      } catch (e) {
        this.$noty.error(`${this.translations.errors.fetch_overview_error}`);
      }
    },
    applyFilters() {
      this.loading = true;
      const filtersToSend = {
        status_in: [],
        client_ids: [],
      };

      if (this.filter_set.status.length > 0) {
        this.filter_set.status.forEach(s => {
          if (s.value !== '') {
            filtersToSend.status_in.push(s.value);
          }
        });
      }

      if (this.filter_set.clients.length > 0) {
        this.filter_set.clients.forEach(client => {
          if (client.value !== '') {
            filtersToSend.client_ids.push(client.value);
          }
        });
      }

      if (this.filter_set.start_end_date) {
        filtersToSend.start_date_from = this.filter_set.start_end_date.start;
        filtersToSend.end_date_until = this.filter_set.start_end_date.end;
      }

      this.$emit('apply-filters', filtersToSend);
      this.loading = false;
    },
    calculateStatusOptions(claimsBatchesOverview) {
      const opts = [];
      const optKeys = {};

      claimsBatchesOverview.forEach(e => {
        opts.push({ value: e.status, text: `${e.status} (${e.count})` });
        optKeys[e.status] = true;
      });

      const requiredOpts = [ 'created', 'submitted', 'error' ]; // always show current claim batches status in use
      requiredOpts.forEach(req => {
        if (!optKeys[req]) {
          opts.push({ value: req, text: `${req} (0)` });
        }
      });

      return opts;
    },
    checkInput(name, { value }) {
      if (value === '') {
        // remove other options when the default is selected
        this.filter_set[name] = [ { value: '', text: this.translations.filters.all_default } ];
      } else if (this.filter_set[name][0].value === '') {
        // auto remove the default option when a value is selected
        this.filter_set[name].splice(0, 1);
      }
    },
    dateRangeValidator(field) {
      if (!field) {
        return true;
      }
      const { start, end } = field;
      return start && end;
    },
  },
};
</script>
